import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Award,
  Check,
  Compass,
  GraduationCap,
  Loader2,
  Pencil,
  Quote,
  RefreshCw,
  Sparkles,
} from 'lucide-react';
import { useLocation } from '@/shared/hooks/useLocation';
import {
  strategyApi,
  type BrandProfileData,
  type BrandPlatformContent,
  type AakerDimension,
} from '../api/strategyApi';
import { strategyKeys } from '../queryKeys';
import { Badge, Button } from '@/shared/ui';
import { PageHeader } from '@/shared/ui/PageHeader';
import { EmptyState } from '@/shared/ui/EmptyState';
import patterns from '@/shared/ui/styles/patterns.module.css';
import styles from './BrandPositioningPage.module.css';

function ArchetypeCompass({ profile }: { profile: BrandProfileData }) {
  const PAD = 24;
  const SIZE = 216;
  const cx = PAD + (profile.masteryScore / 100) * SIZE;
  const cy = PAD + ((100 - profile.structureScore) / 100) * SIZE;
  return (
    <svg
      viewBox="0 0 264 264"
      className={styles.compassSvg}
      aria-label={`Brand compass: ${profile.label}`}
    >
      <rect
        x="1"
        y="1"
        width="262"
        height="262"
        rx="14"
        fill="var(--surface)"
        stroke="var(--border)"
      />
      <line x1="132" y1={PAD} x2="132" y2={PAD + SIZE} stroke="var(--border)" />
      <line x1={PAD} y1="132" x2={PAD + SIZE} y2="132" stroke="var(--border)" />
      <text
        x="132"
        y="16"
        textAnchor="middle"
        fontSize="8"
        fontWeight="700"
        fill="var(--text-muted)"
        letterSpacing="0.08em"
      >
        STRUCTURE
      </text>
      <text
        x="132"
        y="256"
        textAnchor="middle"
        fontSize="8"
        fontWeight="700"
        fill="var(--text-muted)"
        letterSpacing="0.08em"
      >
        FREEDOM
      </text>
      <text
        x="14"
        y="132"
        textAnchor="middle"
        fontSize="8"
        fontWeight="700"
        fill="var(--text-muted)"
        letterSpacing="0.08em"
        transform="rotate(-90 14 132)"
      >
        BELONGING
      </text>
      <text
        x="250"
        y="132"
        textAnchor="middle"
        fontSize="8"
        fontWeight="700"
        fill="var(--text-muted)"
        letterSpacing="0.08em"
        transform="rotate(90 250 132)"
      >
        MASTERY
      </text>
      <text x="36" y="52" fontSize="8" fill="var(--text-secondary)">
        Caregiver
      </text>
      <text x="228" y="52" textAnchor="end" fontSize="8" fill="var(--text-secondary)">
        Sage
      </text>
      <text x="36" y="210" fontSize="8" fill="var(--text-muted)">
        Everyman
      </text>
      <text x="228" y="210" textAnchor="end" fontSize="8" fill="var(--text-muted)">
        Hero
      </text>
      <circle cx={cx} cy={cy} r="11" fill="var(--accent-bg)" />
      <circle cx={cx} cy={cy} r="6" fill="var(--accent)" />
    </svg>
  );
}

interface Emphasis {
  label: string;
  hint: string;
  className: string;
}

// The stored Aaker levels are an emphasis scale, not a quality score. A practice
// reading "Low Excitement" should see a deliberate positioning choice, not a grade.
const EMPHASIS: Record<string, Emphasis> = {
  High: {
    label: 'Signature',
    hint: 'Front and center — this is what your brand leads with.',
    className: styles.emphasisSignature,
  },
  Moderate: {
    label: 'Supporting',
    hint: 'Steadily present — it backs up the traits you lead with.',
    className: styles.emphasisSupporting,
  },
  Low: {
    label: 'Understated',
    hint: 'Deliberately quiet — not a shortfall, just not how your brand competes.',
    className: styles.emphasisUnderstated,
  },
};

function EmphasisBadge({ level }: { level: string }) {
  const emphasis = EMPHASIS[level] ?? EMPHASIS.Moderate;
  return (
    <span className={`${styles.emphasisBadge} ${emphasis.className}`} title={emphasis.hint}>
      {emphasis.label}
    </span>
  );
}

function PersonalityCard({
  profile,
  platform,
}: {
  profile: BrandProfileData;
  platform: BrandPlatformContent | null;
}) {
  return (
    <div className={styles.card}>
      <div className={styles.personalityCardInner}>
        <div className={styles.personalityContent}>
          <div className={patterns.kicker}>
            <Compass size={12} aria-hidden />
            Personality
          </div>
          <div className={styles.badgeRow}>
            <Badge variant="primary">{profile.label}</Badge>
          </div>
          <p className={styles.emphasisIntro}>
            Where each trait sits in your mix — every strong brand leads with a few and holds the
            rest back on purpose.
          </p>
          <div className={styles.personalityList}>
            {profile.aaker?.map((d: AakerDimension) => {
              const note = platform?.personality_notes?.find((n) => n.dimension === d.dimension);
              return (
                <div key={d.dimension} className={styles.personalityRow}>
                  <span className={styles.dimensionLabel}>{d.dimension}</span>
                  <EmphasisBadge level={d.level} />
                  {note && <span className={styles.dimensionNote}>{note.note}</span>}
                </div>
              );
            })}
          </div>
          <p className={styles.akerFootnote}>
            Aaker brand-personality dimensions (Stanford), mapped from your archetype. This is a
            read on emphasis, not a rating — no trait here is better than another.
          </p>
        </div>
        <div className={styles.personalityCompass}>
          <ArchetypeCompass profile={profile} />
        </div>
      </div>
    </div>
  );
}

export function BrandPositioningPage() {
  const navigate = useNavigate();
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  const queryClient = useQueryClient();

  const {
    data: brandProfileRes,
    isLoading: profileLoading,
    isFetching: profileFetching,
  } = useQuery({
    queryKey: strategyKeys.brandProfile(locationId),
    queryFn: () => strategyApi.getBrandProfile(locationId),
    enabled: !!locationId,
    staleTime: 5 * 60 * 1000,
  });
  const brandProfile = brandProfileRes?.data ?? null;

  const { data: platformRes, isLoading: platformLoading } = useQuery({
    queryKey: strategyKeys.brandPlatform(locationId),
    queryFn: () => strategyApi.getBrandPlatform(locationId),
    enabled: !!locationId && !!brandProfile,
    staleTime: 5 * 60 * 1000,
  });
  const platform = platformRes?.data?.content ?? null;

  const generateMutation = useMutation({
    mutationFn: () => strategyApi.generateBrandPlatform(locationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: strategyKeys.brandPlatform(locationId) });
    },
  });

  const platformEnabled = !!locationId && !!brandProfile;
  const isLoading =
    profileLoading || (profileFetching && !brandProfile) || (platformEnabled && platformLoading);
  const isGenerating = generateMutation.isPending;
  const generateError = generateMutation.isError;

  if (isLoading) {
    return (
      <div className={patterns.page}>
        <div className={patterns.panelHeader}>
          <span className={patterns.panelTitle}>Brand &amp; Positioning</span>
        </div>
      </div>
    );
  }

  if (!brandProfile) {
    return (
      <div className={patterns.page}>
        <PageHeader
          title="Brand & Positioning"
          subtitle="Your confirmed brand platform — calibrated from your own answers in the Strategy Builder."
        />
        <EmptyState
          icon={<Compass size={22} aria-hidden />}
          title="Not built yet"
          description="Brand comes from you — a few quick answers inside the Strategy Builder's Brand & Positioning step."
          action={{
            label: 'Go build my brand',
            onClick: () => navigate('/strategy/builder/profile?section=brand'),
          }}
        />
      </div>
    );
  }

  if (!platform) {
    return (
      <div className={patterns.page}>
        <PageHeader
          title="Brand & Positioning"
          subtitle="Your confirmed brand platform — calibrated from your own answers in the Strategy Builder."
          actions={
            <Button
              variant="secondary"
              leftIcon={<Pencil size={14} />}
              onClick={() => navigate('/strategy/builder/profile?section=brand')}
            >
              Edit answers
            </Button>
          }
        />
        {isGenerating ? (
          <div className={styles.card}>
            <div className={styles.generatingState}>
              <Loader2 size={16} className="spin" />
              Generating your brand platform…
            </div>
          </div>
        ) : generateError ? (
          <div className={styles.card}>
            <p className={styles.errorNote}>We couldn't draft this — try again.</p>
          </div>
        ) : (
          <EmptyState
            icon={<Sparkles size={22} aria-hidden />}
            title="Generate your full brand platform"
            description="Turn your archetype into a complete positioning statement, values, and voice guide — in one click."
            actions={
              <Button
                variant="primary"
                leftIcon={<Sparkles size={14} />}
                onClick={() => generateMutation.mutate()}
              >
                Generate brand platform
              </Button>
            }
          />
        )}
      </div>
    );
  }

  return (
    <div className={patterns.page}>
      <PageHeader
        title="Brand & Positioning"
        subtitle="Your confirmed brand platform — calibrated from your own answers in the Strategy Builder."
        actions={
          <>
            <Button
              variant="secondary"
              leftIcon={
                isGenerating ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />
              }
              onClick={() => generateMutation.mutate()}
              disabled={isGenerating}
            >
              Regenerate
            </Button>
            <Button
              variant="secondary"
              leftIcon={<Pencil size={14} />}
              onClick={() => navigate('/strategy/builder/profile?section=brand')}
            >
              Edit answers
            </Button>
          </>
        }
      />

      {/* 1. Brand in a sentence */}
      <div className={styles.card}>
        <div className={patterns.kicker}>
          <Sparkles size={12} aria-hidden />
          Your brand, in a sentence
        </div>
        <p className={styles.essenceText}>{platform.essence}</p>
        <div className={styles.sentenceGrid}>
          <div>
            <div className={styles.subLabel}>Positioning</div>
            <p className={styles.bodyText}>{platform.positioning}</p>
          </div>
          <div>
            <div className={styles.subLabel}>What you stand against</div>
            <p className={styles.bodyText}>{platform.enemy}</p>
          </div>
        </div>
      </div>

      {/* 2. Three-column grid: personality (full width) | values (×2) | voice */}
      <div className={styles.threeColGrid}>
        <div className={styles.colSpan3}>
          <PersonalityCard profile={brandProfile} platform={platform} />
        </div>

        <div className={styles.colSpan2}>
          <div className={styles.card}>
            <div className={patterns.kicker}>
              <Award size={12} aria-hidden />
              Values
            </div>
            <div className={styles.valuesGrid}>
              {platform.values.map((v) => (
                <div key={v.name} className={styles.checkRow}>
                  <Check size={13} className={styles.checkIcon} aria-hidden />
                  <div>
                    <span className={styles.valueName}>{v.name}</span>
                    <span className={styles.valueMeaning}> — {v.meaning}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div>
          <div className={styles.card}>
            <div className={patterns.kicker}>
              <Quote size={12} aria-hidden />
              Voice
            </div>
            <div className={styles.traitsRow}>
              {platform.voice.traits.map((t) => (
                <span key={t} className={styles.trait}>
                  {t}
                </span>
              ))}
            </div>
            <p className={styles.voiceExample}>"{platform.voice.example}"</p>
          </div>
        </div>
      </div>

      {/* Method credit */}
      <div className={patterns.methodCard}>
        <div className={patterns.methodIconWrap}>
          <GraduationCap size={14} aria-hidden />
        </div>
        <div>
          <div className={patterns.methodTitle}>The method behind this</div>
          Built on the <strong>12 brand archetypes</strong> (Jung; popularized for branding by Mark
          &amp; Pearson) and Jennifer Aaker&apos;s peer-reviewed <strong>Brand Personality</strong>{' '}
          dimensions (Stanford), positioning on the Geoffrey Moore template — our own instrument
          inspired by these, a brand-preference read, not a clinical personality test.
        </div>
      </div>
    </div>
  );
}
